/**
 * Who may open which screen. One rule, read by the menu (`getMenuData`) and by
 * the router (`AccessRoute`), so a screen is never in the drawer for someone
 * the URL turns away, nor one URL away for someone the drawer hides it from.
 *
 * Not security: the API enforces permissions regardless. This keeps a customer
 * who types a staff URL from landing on a page that is half-broken by 403s.
 *
 * A new route in `components/root.tsx` needs its path listed below; a test
 * (`route-access.test.ts`) reads root.tsx and fails until it is.
 */
import { matchRoutes } from "react-router-dom";

export type AccessLevel = "everyone" | "staff" | "superuser" | "hosted_email_or_staff";

/** The fields of the signed-in user that decide access. */
export type AccessUser = {
	is_staff: boolean;
	is_superuser: boolean;
	userprofile?: { email_method?: number };
};

/** `userprofile.email_method` of a recruiter whose mail the platform hosts. */
const HOSTED_EMAIL_METHOD = 4;

export const canOpen = (level: AccessLevel, user: AccessUser): boolean => {
	switch (level) {
		case "everyone":
			return true;
		case "staff":
			return user.is_staff;
		case "superuser":
			return user.is_superuser;
		case "hosted_email_or_staff":
			return (
				user.userprofile?.email_method === HOSTED_EMAIL_METHOD || user.is_staff
			);
	}
};

/**
 * Full path pattern of every route inside the logged-in layout, by the level
 * that opens it. Patterns are the ones root.tsx composes from its nested
 * <Route path>s, e.g. "/charts/okr", "/person/:personId/:tab".
 */
const ROUTES_BY_LEVEL: Record<AccessLevel, readonly string[]> = {
	everyone: [
		"/todo",
		"/board/v2",
		"/grading/:assignmentId",
		"/grading/:assignmentId/:personId",
		"/company_search",
		"/person_search",
		"/person/:personId",
		"/person/:personId/:tab",
		"/person/:personId/text",
		"/company/:companyId",
		"/votes/assignment/:assignmentId",
		"/funnel/assignment/:assignmentId",
		"/charts/insights",
		"/config",
		"/config/settings",
		"/config/profile",
		"/config/features",
		"/config/linkedin",
		"/config/email",
		"/config/email/:provider",
		"/config/outlook",
		"/config/gmail",
		"/config/change_password",
		"/config/linkedin/config",
		"/config/linkedin/plugin",
		"/config/linkedin/install",
		"/config/linkedin/plugin/installation",
		"/config/linkedin/plugin/status",
		"/config/group",
		"/config/vacation",
		"/config/integrations",
		"/config/job_ads",
		"/config/api_keys",
	],
	hosted_email_or_staff: ["/email"],
	staff: [
		"/candidate-pool",
		"/docs",
		"/docs/:slug",
		"/tech-docs",
		"/tech-docs/*",
		"/ai-lab",
		"/release-notes",
		"/support",
		"/support/:ticketId",
		"/workflow",
		"/workflow/process",
		"/workflow/:ticketId",
		"/workflow/:ticketId/document/:versionId",
		"/user-management/pending-users",
		"/user-management/active-users",
		"/user-management/customer-licenses",
		"/user-management/deletion-requests",
		"/assignment_stats/:assignmentId",
		// The role's candidate pipeline; the role list links it for staff only.
		"/candidates/:assignmentId",
		"/company_sales",
		"/job",
		// Instance-wide settings; the navbar also asks for the "Artemis" group.
		"/system-settings",
		// Scraped LinkedIn postings; the company page's job list links here.
		"/linkedin_job_posting",
		"/prod_import",
		"/staging-deploy",
		"/staging-overview",
		"/charts/assignments_overview",
		"/charts/assignment_charts/:assignmentId",
		// Linked from the Roles screen, which staff open.
		"/charts/grade-connect-queues/:assignmentId",
		"/charts/infra_costs",
		"/charts/okr",
	],
	superuser: [
		"/cv-browser",
		"/person/latest",
		"/fingerprint-sessions",
		"/fingerprint-sessions/:sessionId",
		"/dev/runtime-error",
		"/charts/lic_keys",
		"/charts/rotating_proxies",
		"/charts/grade-connect-queues",
		"/charts/bot_board",
		"/charts/celery_tasks",
		"/charts/bot/:botId",
		"/charts/person_analysis",
		"/charts/git_stats",
		"/charts/llm_performance",
		"/charts/llm_pricing",
		"/charts/data_explorer",
		"/charts/human-operator-agreement",
		"/charts/llm_evaluation",
		"/charts/llm_templates",
		"/charts/extractor_prompts",
		"/charts/random-persons",
		"/charts/latest-persons",
		"/charts/accounts",
		"/charts/latest-voyager-logs",
		"/charts/voyager-log-stats",
		"/charts/llm-cache-stats",
		"/charts/latest-get-profile-queue",
		"/charts/bot_stats",
		"/charts/ml_stats",
		"/charts/rerank_judge",
		"/charts/vote_stats",
		"/charts/userscraping_stats",
		"/charts/job_stats",
		"/charts/botfactory_stats",
		"/charts/third_party_usage",
		"/charts/thumb_stats",
		"/charts/email_stats",
		"/charts/connect_stats",
		"/charts/pitch_stats",
		"/charts/database_load",
		"/charts/advisory_locks",
		"/charts/index_health",
		"/charts/database_size",
		"/charts/hot_database_tables",
		"/charts/db_backups",
		"/charts/send_invite_queue",
		"/charts/inmail_cost_queue",
		"/charts/contactinfo_tng_queue",
		"/charts/kpis",
		"/charts/loki_logs",
		"/charts/pydoll-monitor",
		"/charts/captcha-monitor",
		"/charts/email_journal",
		"/charts/postfix_delivery_events",
		"/charts/ses_dashboard",
		"/charts/screen_tiles",
		"/charts/plugin_work_log",
		"/charts/cookies",
		"/charts/theme_colors",
		"/charts/vote_testsets",
		"/charts/llm_batches",
		"/charts/assignment-llm-skills",
		"/charts/llm_cache",
		"/charts/provider_batch_jobs",
		"/charts/assignment-llm-agreement",
		"/charts/email_sync",
		"/charts/assignment_locations",
		"/charts/ostmd/:id?",
		"/charts/elasticsearch_status",
		"/charts/jobylon_status",
	],
};

/** Every gated path pattern with its level. */
export const ROUTE_ACCESS: Readonly<Record<string, AccessLevel>> = Object.fromEntries(
	Object.entries(ROUTES_BY_LEVEL).flatMap(([level, paths]) =>
		paths.map((path): [string, AccessLevel] => [path, level as AccessLevel])
	)
);

const ROUTE_PATTERNS = Object.keys(ROUTE_ACCESS).map((path) => ({ path }));

/**
 * The level that opens the screen at `pathname`, picked by the router's own
 * ranking (a static segment beats a param). Undefined when no route is listed,
 * in which case no screen renders there anyway.
 */
export const accessLevelForPath = (pathname: string): AccessLevel | undefined => {
	const matches = matchRoutes(ROUTE_PATTERNS, pathname);
	const pattern = matches?.at(-1)?.route.path;
	return pattern === undefined ? undefined : ROUTE_ACCESS[pattern];
};

/** Whether `user` may open the screen at `pathname`; true where no route is listed. */
export const canOpenPath = (pathname: string, user: AccessUser): boolean => {
	const level = accessLevelForPath(pathname);
	return level === undefined || canOpen(level, user);
};
